/**
 * [TD-68 Section 2.4] Pre-Draft Hub - unified commissioner share surface.
 *
 * Replaces the legacy `Invite Link` card in DraftLobby.jsx. Subsumes:
 *   • QR code (theme-aware via fgColor/bgColor swap)
 *   • Bare invite code + Copy Code button
 *   • Full join URL + Copy Link button (preserves `lobby-copy-invite-btn` testid)
 *   • Email-invite textarea + Send button (calls
 *     POST /api/commissioner/{league_id}/send-invite-emails)
 *
 * Per Verdict 1.A: commissioner-only render. Non-commissioner managers see
 * nothing here; they already see the invite code on the Dashboard tile.
 */
import { useState, useMemo } from "react";
import axios from "axios";
import QRCode from "react-qr-code";
import { Copy, Check, QrCode, Mail, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { authHeaders } from "../../contexts/AuthContext";
import { parseInviteEmails, INVITE_EMAIL_REGEX_SOURCE } from "./inviteEmailParser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// [TD-68 Verdict 4.A] Mirrors backend constants. Kept in sync manually.
const PER_CALL_CAP = 25;
const LIFETIME_CAP = 100;

export default function PreDraftHub({ league, isCommissioner, isDark }) {
  const [emailInput, setEmailInput] = useState("");
  const [sending, setSending] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  if (!isCommissioner) return null;

  const joinUrl = `${window.location.origin}/join/${league.invite_code}`;
  const sentSoFar = Number(league?.settings?.invite_emails_sent_count || 0);
  const lifetimeRemaining = Math.max(0, LIFETIME_CAP - sentSoFar);

  const { valid: parsedValid, invalid: parsedInvalid } = useMemo(
    () => parseInviteEmails(emailInput),
    [emailInput],
  );
  // [TD-68 Section 2.4 - slicing per founder interpretation] If client has
  // more than 25 valid entries, slice to first 25; surface inline hint.
  const willSend = parsedValid.slice(0, Math.min(PER_CALL_CAP, lifetimeRemaining));
  const truncatedByCallCap = parsedValid.length > PER_CALL_CAP;
  const truncatedByLifetime =
    parsedValid.length > lifetimeRemaining && lifetimeRemaining < PER_CALL_CAP;

  // Palette tokens
  const text = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";
  const muted = isDark ? "text-[rgba(255,255,255,0.55)]" : "text-[rgba(0,0,0,0.55)]";
  const subtle = isDark ? "text-[rgba(255,255,255,0.40)]" : "text-[rgba(0,0,0,0.45)]";
  const fieldBg = isDark
    ? "bg-[#111111] border-[#252525] text-[#F5F5F5]"
    : "bg-white border-black/[0.10] text-[#0A0A0A]";
  const ghostBtn = isDark
    ? "bg-white/[0.06] hover:bg-white/[0.12] text-[#F5F5F5]"
    : "bg-black/[0.06] hover:bg-black/[0.12] text-[#0A0A0A]";

  const fgColor = isDark ? "#F5F5F5" : "#0A0A0A";
  const bgColor = isDark ? "#1C1C1C" : "#FFFFFF";

  const copy = async (value, setFlag) => {
    try {
      await navigator.clipboard.writeText(value);
      setFlag(true);
      setTimeout(() => setFlag(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleSend = async () => {
    if (willSend.length === 0 || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/commissioner/${league.id}/send-invite-emails`,
        { emails: willSend },
        { headers: authHeaders() },
      );
      const { sent = 0, failed = [] } = data || {};
      if (sent > 0 && failed.length === 0) {
        toast.success(`Sent ${sent} invite${sent === 1 ? "" : "s"}`);
        setEmailInput("");
      } else if (sent > 0 && failed.length > 0) {
        // [Email item 6] Say WHY the rest failed, not just how many.
        toast.message(`Sent ${sent} · ${failed.length} failed`, {
          description: failed[0]?.message || failed[0]?.reason,
        });
        // Preserve failed addresses in the textarea for retry.
        setEmailInput(failed.map((f) => f.email).join("\n"));
      } else {
        // Every send failed - the server tells us the real reason
        // (no key, unverified domain, rate limit, bad address).
        toast.error(
          failed[0]?.message || failed[0]?.reason || "All invites failed",
          { duration: 8000 },
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invites");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="glass-card rounded-2xl p-5" data-testid="pre-draft-hub-root">
      <h2 className={`font-display font-bold text-base uppercase flex items-center gap-2 mb-1 ${text}`}>
        <QrCode size={16} className="text-[#FF6B00]" /> Share Your League
      </h2>
      <p className={`font-body text-xs mb-4 ${muted}`}>
        Three ways to bring managers in - QR, code, or email.
      </p>

      {/* Block A - QR + code/link side-by-side */}
      <div className="flex flex-col md:flex-row gap-5 items-stretch md:items-start">
        <div
          data-testid="pre-draft-hub-qr"
          className="rounded-lg p-3 self-center md:self-start"
          style={{ background: bgColor }}
        >
          <QRCode
            value={joinUrl}
            size={140}
            fgColor={fgColor}
            bgColor={bgColor}
            level="M"
          />
        </div>

        <div className="flex-1 space-y-3 min-w-0">
          <div>
            <label className={`block font-display font-bold text-[10px] uppercase tracking-wider mb-1 ${subtle}`}>
              Invite Code
            </label>
            <div
              data-testid="pre-draft-hub-code-row"
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${fieldBg}`}
            >
              <span className="font-mono text-[#FF6B00] flex-1 truncate">{league.invite_code}</span>
              <button
                data-testid="pre-draft-hub-copy-code-btn"
                onClick={() => copy(league.invite_code, setCodeCopied)}
                className={`flex items-center gap-1 px-2 py-1 rounded-md font-display font-bold text-[10px] uppercase tracking-wider transition-all ${ghostBtn}`}
              >
                {codeCopied ? <Check size={11} className="text-[#00C853]" /> : <Copy size={11} />}
                {codeCopied ? "Copied" : "Copy Code"}
              </button>
            </div>
          </div>

          <div>
            <label className={`block font-display font-bold text-[10px] uppercase tracking-wider mb-1 ${subtle}`}>
              Invite Link
            </label>
            <div
              data-testid="pre-draft-hub-link-row"
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${fieldBg}`}
            >
              <LinkIcon size={12} className={subtle + " shrink-0"} />
              <span className="font-mono text-xs flex-1 truncate">{joinUrl}</span>
              <button
                data-testid="lobby-copy-invite-btn"
                onClick={() => copy(joinUrl, setLinkCopied)}
                className={`flex items-center gap-1 px-2 py-1 rounded-md font-display font-bold text-[10px] uppercase tracking-wider transition-all ${ghostBtn}`}
              >
                {linkCopied ? <Check size={11} className="text-[#00C853]" /> : <Copy size={11} />}
                {linkCopied ? "Copied" : "Copy Link"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Block B - Email invites */}
      <div
        data-testid="pre-draft-hub-email-block"
        className={`mt-5 pt-5 border-t ${isDark ? "border-white/[0.08]" : "border-black/[0.08]"}`}
      >
        <div className="flex items-center justify-between mb-2">
          <label className={`flex items-center gap-1.5 font-display font-bold text-[10px] uppercase tracking-wider ${subtle}`}>
            <Mail size={11} /> Email Invites
          </label>
          <span className={`font-body text-[10px] ${subtle}`}>
            {sentSoFar}/{LIFETIME_CAP} sent · {lifetimeRemaining} remaining
          </span>
        </div>
        <textarea
          data-testid="pre-draft-hub-email-textarea"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          placeholder="one email per line - or comma/semicolon-separated (max 25 per send)"
          rows={3}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          className={`w-full rounded-lg border px-3 py-2 font-body text-sm resize-y ${fieldBg} focus:border-[#FF6B00] outline-none transition-colors`}
          data-regex={INVITE_EMAIL_REGEX_SOURCE}
        />
        <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
          <div className={`font-body text-xs ${muted} flex items-center gap-3`}>
            <span>Valid: <span data-testid="pre-draft-hub-valid-count" className="text-[#00C853] font-bold">{parsedValid.length}</span></span>
            <span>Invalid: <span data-testid="pre-draft-hub-invalid-count" className="text-[#E03030] font-bold">{parsedInvalid.length}</span></span>
            {truncatedByCallCap && (
              <span className={subtle}>· first {PER_CALL_CAP} of {parsedValid.length} will be sent</span>
            )}
            {truncatedByLifetime && !truncatedByCallCap && (
              <span className={subtle}>· only {lifetimeRemaining} send{lifetimeRemaining === 1 ? "" : "s"} left in lifetime cap</span>
            )}
          </div>
          <button
            data-testid="pre-draft-hub-send-emails-btn"
            disabled={willSend.length === 0 || sending || lifetimeRemaining === 0}
            onClick={handleSend}
            className="bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-40 disabled:cursor-not-allowed text-white font-display font-black uppercase tracking-wider text-xs px-4 py-2 rounded-lg transition-all active:scale-95"
          >
            {sending
              ? "Sending..."
              : lifetimeRemaining === 0
                ? "Lifetime cap reached"
                : `Send ${willSend.length} Invite${willSend.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
